import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

/**
 * Reading modal for a single managed-account Terms & Conditions document. The files in
 * `public/tnc/ma/` are HTML fragments (no <html>/<head>), so we fetch the fragment and
 * render it inside a styled `.ma-tnc-doc` reading pane rather than opening the raw,
 * unstyled file in a new tab. First-party trusted static content, so dangerouslySet is
 * acceptable here (no user input).
 */
const ManagedAccountTncViewer = ({ doc, isOpen, accepted = false, disabled = false, onClose, onAccept }) => {
  const [html, setHtml] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const loadedUrl = useRef("");

  useEffect(() => {
    if (!isOpen || !doc?.url) return undefined;
    if (loadedUrl.current === doc.url) { setStatus("ready"); return undefined; }
    let active = true;
    setStatus("loading");
    setHtml("");
    fetch(doc.url)
      .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.text(); })
      .then((text) => {
        if (!active) return;
        setHtml(text);
        loadedUrl.current = doc.url;
        setStatus("ready");
      })
      .catch(() => { if (active) setStatus("error"); });
    return () => { active = false; };
  }, [isOpen, doc?.url]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" centered scrollable className="ma-tnc-viewer" data-testid="ma-tnc-viewer">
      <ModalHeader toggle={onClose} className="ma-tnc-viewer__header">{doc?.label}</ModalHeader>
      <ModalBody className="ma-tnc-viewer__body">
        {status === "loading" ? (
          <div className="ma-tnc-viewer__state" role="status">Loading document…</div>
        ) : null}
        {status === "error" ? (
          <div className="ma-tnc-viewer__state ma-tnc-viewer__state--error" role="alert">
            We couldn&apos;t load this document.{" "}
            <a href={doc?.url} target="_blank" rel="noopener noreferrer">Open it in a new tab</a> instead.
          </div>
        ) : null}
        {status === "ready" ? (
          // eslint-disable-next-line react/no-danger
          <div className="ma-tnc-doc" dangerouslySetInnerHTML={{ __html: html }} />
        ) : null}
      </ModalBody>
      <ModalFooter className="ma-tnc-viewer__footer">
        <Button type="button" color="link" className="ma-tnc-viewer__close" onClick={onClose}>Close</Button>
        {accepted ? (
          <span className="ma-tnc-viewer__accepted" aria-live="polite">Accepted</span>
        ) : (
          <Button
            type="button"
            color="dark"
            disabled={disabled}
            data-testid="ma-tnc-viewer-accept"
            onClick={() => { onAccept?.(); onClose?.(); }}
          >
            I have read &amp; accept
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ManagedAccountTncViewer;
